import { beginAppSelection } from "@/performance/startupTiming";
// Owns the SPA's URL ⇄ state sync: the current route (plus the query/fragment
// param: source), programmatic navigation, and the hashchange / popstate
// listeners. Navigation is PATHNAME-first — canonical routes live in the
// path, and the hash fragment is reserved for a short list of transient data
// (continuation_*/waitlist). Legacy `route=`-carrying hash URLs are
// canonicalized to real paths on mount and on hashchange (read-only backward
// compat; no new `route=` URL is generated).
import { useCallback, useEffect, useRef, useState } from "react";
import { hasNavigationBlocker, requestNavigation } from "./navigationGuard";
import { parseFragment } from "../../api/endpoint";
import {
  canonicalizeLegacyHashRoute,
  parseRoute,
  routeFromPathAndSearch,
} from "./routeParser";
import {
  resolveHomeView,
  readHomeView,
  writeHomeView,
  homeViewTarget,
  type HomeView,
} from "@/components/personal-apps/homeView";
import { isDiscoveryWorkspaceEnabled } from "@/config/featureFlags";
import {
  parseAppDetailsUrl,
  detailsNavigationTarget,
  migrateLegacyDetailsUrl,
  parseDiscoveryUrl,
} from "@/components/personal-apps/discovery/discoveryUrl";
import type {
  AppDetailsState,
  AppDetailsTab,
  DiscoverySelection,
  DiscoveryUrlState,
} from "@/components/personal-apps/discovery/types";

export interface NavigateOptions {
  discoveryIntent?: { requestId: string; appId: string; publishedVersion: string };
  source?: string;
  replace?: boolean;
  launchIntent?: {
    target: string;
    ref?: string;
    origin?: "billing_resume";
    paid?: boolean;
  };
}

export interface AppNavigation {
  route: string;
  /** Capsule source carried by the URL (`source=` query; legacy fragment) for authoring and `/apps/new`. */
  addSource: string | null;
  /**
   * Which Home split-view arrangement the URL asks for. Derived from the
   * `view` query on `/` — kept beside `route` (rather than inside it) exactly
   * like `addSource`, so every existing route comparison keeps working
   * unchanged.
   */
  homeView: HomeView;
  /** Selection/filter intent only; reading this never starts an application. */
  discoveryState: DiscoveryUrlState | null;
  detailState: AppDetailsState | null;
  openDetails: (target: DiscoverySelection, tab?: AppDetailsTab) => void;
  closeDetails: () => void;
  setDetailTab: (tab: AppDetailsTab) => void;
  navigate: (route: string, opts?: NavigateOptions) => void;
}

// Source is a screen-routing-agnostic query param in the canonical form; a
// legacy `source=` fragment param is only a fallback read (migration).
function sourceFromUrl(): string | null {
  const fromQuery = new URLSearchParams(window.location.search).get("source");
  if (fromQuery) return fromQuery;
  return parseFragment().source ?? null;
}

function parseInternalTarget(rawRoute: string): {
  pathname: string;
  search: string;
} {
  const qIdx = rawRoute.indexOf("?");
  return {
    pathname: qIdx >= 0 ? rawRoute.slice(0, qIdx) : rawRoute,
    search: qIdx >= 0 ? rawRoute.slice(qIdx) : "",
  };
}

function homeViewFromLiveUrl(): HomeView {
  return homeViewForTarget(window.location.pathname, window.location.search);
}

function preferredHomeTarget(pathname: string, search: string): string {
  // Restore only a bare Home visit. Deep links, queries and non-Home routes win.
  if (pathname !== "/" || search) return search;
  const view = readHomeView();
  return view === "split" ? "" : homeViewTarget(view).slice(1);
}

function discoveryStateFromLiveUrl(): DiscoveryUrlState | null {
  return parseDiscoveryUrl(
    window.location.pathname,
    window.location.search,
    isDiscoveryWorkspaceEnabled(),
  );
}

// Only these paths consume the view. Anywhere else the value is ignored, so
// resolving it there would only invent state nobody reads.
function homeViewForTarget(pathname: string, search: string): HomeView {
  if (pathname === "/") {
    const view = resolveHomeView(pathname, search);
    const requested = new URLSearchParams(search).get("view");
    if (requested === "split" || requested === "my-apps" || requested === "discover") writeHomeView(view);
    return view;
  }
  return "split";
}

function canonicalizeDetailsUrl() {
  const target = migrateLegacyDetailsUrl(
    window.location.pathname,
    window.location.search,
  );
  if (target) history.replaceState(history.state, "", target);
}
function detailsFromLiveUrl() {
  return parseAppDetailsUrl(window.location.pathname, window.location.search);
}

export function useAppNavigation(): AppNavigation {
  const index = useRef<number>(history.state?.atoNavigationIndex ?? 0);
  const restoring = useRef<(() => void) | null>(null);
  const acceptedPop = useRef(false);
  const [route, setRoute] = useState<string>(() => parseRoute());
  const [addSource, setAddSource] = useState<string | null>(() =>
    sourceFromUrl(),
  );
  const [homeView, setHomeView] = useState<HomeView>(() =>
    homeViewForTarget(window.location.pathname, preferredHomeTarget(window.location.pathname, window.location.search)),
  );
  const [detailState, setDetailState] = useState(detailsFromLiveUrl);
  const [discoveryState, setDiscoveryState] = useState(
    () => parseDiscoveryUrl(window.location.pathname, preferredHomeTarget(window.location.pathname, window.location.search), isDiscoveryWorkspaceEnabled()),
  );

  // Mount: resolve the route (incl. any legacy hash route), canonicalize a
  // legacy `#route=...` URL to a real path, then re-sync state to the
  // canonical URL. Canonicalization also strips the retired Desktop Runtime
  // endpoint/token fragment, which no longer has a consumer.
  useEffect(() => {
    canonicalizeLegacyHashRoute();
    canonicalizeDetailsUrl();
    const search = preferredHomeTarget(window.location.pathname, window.location.search);
    history.replaceState(
      { ...history.state, atoNavigationIndex: index.current },
      "",
      window.location.pathname + search + window.location.hash,
    );
    setRoute(parseRoute());
    setAddSource(sourceFromUrl());
    setHomeView(homeViewFromLiveUrl());
    setDiscoveryState(discoveryStateFromLiveUrl());
    setDetailState(detailsFromLiveUrl());
  }, []);

  // Hash change: for legacy links and transient fragments. Re-sync; only when
  // a `route` reappears in the fragment (an old link) do we canonicalize it
  // to a real path.
  useEffect(() => {
    const onHash = () => {
      canonicalizeLegacyHashRoute();
      canonicalizeDetailsUrl();
      setRoute(parseRoute());
      setAddSource(sourceFromUrl());
      setHomeView(homeViewFromLiveUrl());
      setDiscoveryState(discoveryStateFromLiveUrl());
      setDetailState(detailsFromLiveUrl());
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  // Browser back/forward through a real-path route — `history.pushState`
  // (navigate()) does NOT fire "hashchange", so without this, clicking Back
  // after a path navigation would silently move the address bar without
  // re-rendering to match. Same sync logic as the hashchange handler above;
  // the two listeners cover disjoint triggers.
  useEffect(() => {
    const onPopState = () => {
      if (restoring.current) {
        const ask = restoring.current;
        restoring.current = null;
        ask();
        return;
      }
      canonicalizeDetailsUrl();
      const targetIndex: number | undefined = history.state?.atoNavigationIndex;
      if (
        !acceptedPop.current &&
        hasNavigationBlocker() &&
        targetIndex !== undefined &&
        targetIndex !== index.current
      ) {
        const delta = targetIndex - index.current;
        restoring.current = () =>
          requestNavigation(() => {
            acceptedPop.current = true;
            history.go(delta);
          });
        history.go(-delta);
        return;
      }
      acceptedPop.current = false;
      index.current = targetIndex ?? index.current;
      setRoute(parseRoute());
      setAddSource(sourceFromUrl());
      setHomeView(homeViewFromLiveUrl());
      setDiscoveryState(discoveryStateFromLiveUrl());
      setDetailState(detailsFromLiveUrl());
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  // Navigate to a REAL path. The hash is never carried across a normal
  // navigation (`/a/app#stale → /settings` lands on `/settings`). A previous
  // screen's fragment (stale credentials, a prior source) is deliberately
  // dropped so no screen inherits another's state.
  const navigate = useCallback((rawRoute: string, opts?: NavigateOptions) => {
    const rawTarget = parseInternalTarget(rawRoute);
    const migrated = migrateLegacyDetailsUrl(
      rawTarget.pathname,
      rawTarget.search,
    );
    const target = migrated ? parseInternalTarget(migrated) : rawTarget;
    if (opts?.source) {
      target.search +=
        (target.search.includes("?") ? "&" : "?") +
        `source=${encodeURIComponent(opts.source)}`;
    }
    target.search = preferredHomeTarget(target.pathname, target.search);
    requestNavigation(() => {
      const nextDetails = parseAppDetailsUrl(target.pathname, target.search);
      const previousDetails = detailsFromLiveUrl();
      const detailOrigin =
        nextDetails && !previousDetails && !opts?.replace
          ? window.location.pathname + window.location.search
          : nextDetails && previousDetails
            ? history.state?.atoDetailOrigin
            : undefined;
      const detailOriginIndex =
        nextDetails && !previousDetails && !opts?.replace
          ? index.current
          : nextDetails && previousDetails
            ? history.state?.atoDetailOriginIndex
            : undefined;
      // Details are an overlay on the same live Discover use. Preserve its
      // intent through tab changes/reload; ordinary Library/direct navigation clears it.
      const discoveryIntent = opts?.discoveryIntent ?? (nextDetails ? history.state?.atoDiscoveryIntent : undefined);
      const nextDiscovery = parseDiscoveryUrl(target.pathname, target.search, isDiscoveryWorkspaceEnabled());
      const previousDiscovery = discoveryStateFromLiveUrl();
      if (JSON.stringify(nextDiscovery?.selection) !== JSON.stringify(previousDiscovery?.selection)) beginAppSelection();
      if (opts?.replace) {
        history.replaceState(
          {
            ...history.state,
            atoNavigationIndex: index.current,
            atoDetailOrigin: detailOrigin,
            atoDetailOriginIndex: detailOriginIndex,
            atoLaunchIntent: opts?.launchIntent,
            atoDiscoveryIntent: discoveryIntent,
          },
          "",
          target.pathname + target.search,
        );
      } else {
        index.current += 1;
        history.pushState(
          {
            atoNavigationIndex: index.current,
            atoDetailOrigin: detailOrigin,
            atoDetailOriginIndex: detailOriginIndex,
            atoLaunchIntent: opts?.launchIntent,
            atoDiscoveryIntent: discoveryIntent,
          },
          "",
          target.pathname + target.search,
        );
      }
      setDetailState(nextDetails);
      setRoute(routeFromPathAndSearch(target.pathname, target.search));
      setAddSource(opts?.source ?? null);
      // Query-only view changes must commit with the guarded URL change.
      setHomeView(homeViewForTarget(target.pathname, target.search));
      setDiscoveryState(
        parseDiscoveryUrl(
          target.pathname,
          target.search,
          isDiscoveryWorkspaceEnabled(),
        ),
      );
    });
  }, []);

  const openDetails = useCallback(
    (target: DiscoverySelection, tab: AppDetailsTab = "overview") => {
      navigate(
        detailsNavigationTarget(
          target,
          tab,
          window.location.pathname + window.location.search,
        ),
      );
    },
    [navigate],
  );
  const closeDetails = useCallback(() => {
    if (
      history.state?.atoDetailOriginIndex !== undefined &&
      history.state.atoDetailOriginIndex < index.current
    ) {
      // onPopState owns the guard, so Back and the close control behave alike.
      history.go(history.state.atoDetailOriginIndex - index.current);
      return;
    }
    const url = new URL(window.location.href);
    url.searchParams.delete("detail");
    url.searchParams.delete("tab");
    url.searchParams.delete("start_error");
    url.searchParams.delete("qa_report");
    url.searchParams.delete("qa_attempt");
    navigate(url.pathname + url.search, { replace: true, discoveryIntent: history.state?.atoDiscoveryIntent });
  }, [navigate]);
  const setDetailTab = useCallback(
    (tab: AppDetailsTab) => {
      const details = detailsFromLiveUrl();
      if (details)
        navigate(
          detailsNavigationTarget(
            details.target,
            tab,
            window.location.pathname + window.location.search,
          ),
          { replace: true },
        );
    },
    [navigate],
  );
  return {
    route,
    addSource,
    homeView,
    discoveryState,
    detailState,
    openDetails,
    closeDetails,
    setDetailTab,
    navigate,
  };
}
